import { createInertiaApp, router } from '@inertiajs/react'
import { createElement, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { installStandaloneRecovery } from '@/lib/standalone-recovery'
import { claimPendingQuizAnswers } from '@/lib/guest-quiz'
import type { SharedProps } from '@/types/inertia'

// A guest's answers are claimed once `current_user` becomes present. That can
// happen either via a genuine full-page load (setup() below) or, more
// commonly, because signup/login redirect back through Inertia's own
// client-side visit-following rather than a hard browser reload — so this
// must also run on every completed visit, not just app boot.
router.on('success', (event) => {
  const props = event.detail.page.props as unknown as SharedProps
  claimPendingQuizAnswers(props.current_user)
})

type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

// Recover iOS Home Screen (standalone PWA) sessions from a dead/suspended
// WebKit context. Client-only — never imported by the SSR entrypoint.
installStandaloneRecovery()

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }
    return page
  },

  setup({ el, App, props }) {
    const initialProps = props.initialPage.props as unknown as SharedProps
    claimPendingQuizAnswers(initialProps.current_user)

    if (el) {
      createRoot(el).render(
        createElement(ErrorBoundary, null, createElement(App, props)),
      )
    } else {
      console.error(
        'Missing root element. Move `vite_typescript_tag "inertia"` into an Inertia-specific layout.',
      )
    }
  },
})
